import React, { useContext, useEffect, useState } from "react";
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select";
import Column from "@amzn/meridian/column";
import Button from "@amzn/meridian/button";
import "../styles/OpsClockView.css";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import DatePicker from "@amzn/meridian/date-picker";
import { SettingsContext } from "./SettingsContext";
import { MODAL_ERROR } from "../SettingsConstant";
import { FormattedMessage } from "react-intl";
import Text from "@amzn/meridian/text";
import OpsClockProcessData from "./OpsClockProcessData";
import UpdatePlanResponse from "./UpdatePlanResponse";
import OpsClockUpload from "./OpsClockUpload";
import { getStationCode } from "../../network/NetworkUtil";
import Icon from "@amzn/meridian/icon";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import Tooltip from "@amzn/meridian/tooltip";
import { validateTimes, getCountryCode } from "../../util/Util";
import {
  PROCESS_ORDER,
  OPSCLOCK_UPLOAD_ENABLED_COUNTRIES,
} from "../../util/Constants";
import { addMilliseconds, parse } from "date-fns";

const OpsClockView = (props) => {
  const [cycle, setCycle] = useState("CYCLE_1");
  const { viewModel, dispatch } = useContext(SettingsContext);
  const [currentDate, setCurrentDate] = useState(
    viewModel.currentOpsClockDate
      ? viewModel.currentOpsClockDate
      : new Date().toISOString(),
  );
  const [opsClockNewData, setOpsClockNewData] = useState(null);
  const [opsClockOldData, setOpsClockOldData] = useState(null);
  const [timeZone, setTimeZone] = useState(null);
  const [isUploadEnabled, setIsUploadEnabled] = useState(false);
  const allCycles = viewModel.allCycles;
  const isUserAuthorisedForSettingsEdit =
    props.props.isUserAuthorisedForSettingsEdit;

  const handleChangeDispatchDate = (newDate) => {
    setCurrentDate(newDate);
    dispatch({
      type: "LOAD_OPS_CLOCK_DATA",
      data: { dispatcher: dispatch, dispatchDate: newDate },
    });
  };

  const handleClockEventTimeOverride = (
    newTime,
    oldEpoch,
    processName,
    cycle,
    datetime,
    timeType,
  ) => {
    let opsClock = JSON.parse(JSON.stringify(opsClockNewData));
    const [date] = datetime.split(" ");
    let newDateString = date + " " + newTime;
    let newDate = new Date(newDateString);
    let resetDate = new Date(opsClock.dispatchDate);

    // This makes sure that the Reset Date is same as dispatchDate
    // For stations in timezone < UTC + 0, reset time = dispatch date - 1
    // For stations in timezone >= UTC + 0, reset time = dispatch date
    // If local time is ahead of UTC (positive offset like UTC+2), .getTimezoneOffset() will return a negative value (-120 for UTC+2).
    // If local time is behind UTC (negative offset like UTC-7), .getTimezoneOffset() will return a positive value (420 for UTC-7).
    if (resetDate && resetDate.getTimezoneOffset() > 0) {
      resetDate.setDate(resetDate.getDate() + 1);
    }

    let oldDate = new Date(datetime);
    let newEpoch;

    const timeZoneConvertedDate = new Date(
      newDate.toLocaleString("en-US", { timeZone }),
    );
    const timeZoneOffset = newDate.getTime() - timeZoneConvertedDate.getTime();

    const resetTime =
      viewModel.resetTime &&
      viewModel.resetTime.settingsData &&
      viewModel.resetTime.settingsData.shiftTimeRange &&
      viewModel.resetTime.settingsData.shiftTimeRange.endTime;
    const parsedResetTime = addMilliseconds(
      parse(
        `${resetTime.hour}:${resetTime.minute}:${resetTime.second}`,
        "HH:mm:ss",
        resetDate,
      ),
      timeZoneOffset,
    );

    if (oldEpoch == null) {
      let newDateTimeZone = new Date(newDate.getTime() + timeZoneOffset);
      newEpoch = newDateTimeZone.getTime() / 1000;
    } else {
      newEpoch = oldEpoch + (newDate.getTime() - oldDate.getTime()) / 1000;
    }

    // Initialize overrideClockEvents if it doesn't exist
    if (!opsClock.overrideClockEvents) {
      opsClock.overrideClockEvents = [];
    }

    // If no overrides exist, copy all from clockEvents first
    if (
      opsClock.overrideClockEvents.length === 0 &&
      opsClock.clockEvents &&
      Array.isArray(opsClock.clockEvents)
    ) {
      opsClock.overrideClockEvents = [...opsClock.clockEvents];
    }

    // Update overrideClockEvents (not clockEvents) for Save functionality
    if (
      opsClock.overrideClockEvents &&
      Array.isArray(opsClock.overrideClockEvents)
    ) {
      opsClock.overrideClockEvents.forEach((ce) => {
        if (ce.process === processName && ce.cycleName === cycle) {
          if (timeType === "startTime")
            ce.timeIntervals[0].startTime =
              newTime === ""
                ? getOldTimeData(ce, "startTime")
                : validateTimes(
                    new Date(newEpoch * 1000),
                    currentDate,
                    viewModel.opsClockData,
                    true,
                    processName,
                    parsedResetTime,
                  ) / 1000;
          else
            ce.timeIntervals[0].endTime =
              newTime === ""
                ? getOldTimeData(ce, "endTime")
                : validateTimes(
                    new Date(newEpoch * 1000),
                    currentDate,
                    viewModel.opsClockData,
                    true,
                    processName,
                    parsedResetTime,
                  ) / 1000;
        }
      });
    }

    setOpsClockNewData(opsClock);
  };

  const handleUpdateOpsClock = () => {
    let save = true;
    const newOpsClockData = JSON.parse(JSON.stringify(opsClockNewData));

    // Initialize overrideClockEvents if it doesn't exist
    if (!newOpsClockData.overrideClockEvents) {
      newOpsClockData.overrideClockEvents = [];
    }

    // If no overrides exist, copy all from clockEvents first
    if (
      newOpsClockData.overrideClockEvents.length === 0 &&
      newOpsClockData.clockEvents &&
      Array.isArray(newOpsClockData.clockEvents)
    ) {
      newOpsClockData.overrideClockEvents = [...newOpsClockData.clockEvents];
    }

    // Work only with overrideClockEvents for Save operations
    if (
      newOpsClockData.overrideClockEvents &&
      Array.isArray(newOpsClockData.overrideClockEvents)
    ) {
      for (let i = 0; i < newOpsClockData.overrideClockEvents.length; i++) {
        let ce = newOpsClockData.overrideClockEvents[i];
        if (
          ce.timeIntervals[0].startTime == null &&
          ce.timeIntervals[0].endTime == null
        ) {
          newOpsClockData.overrideClockEvents.splice(i, 1);
          i--;
        } else if (
          ce.timeIntervals[0].startTime == null ||
          ce.timeIntervals[0].endTime == null
        ) {
          dispatch({
            type: "UPDATE_PLAN_RESPONSE",
            data: {
              showUpdatePlanResponseModal: MODAL_ERROR,
              message:
                "Please specify both start and end time for " + ce.process,
            },
          });
          save = false;
          break;
        }
      }
    }

    // Keep original clockEvents unchanged
    newOpsClockData.clockEvents = viewModel.opsClockData.opsClock.clockEvents;
    newOpsClockData.breaks = null;

    if (save)
      dispatch({
        type: "UPDATE_OPS_CLOCK_REQUEST",
        data: { dispatcher: dispatch, planData: newOpsClockData },
      });
  };

  const handleRemoveAllFilters = () => {
    setCurrentDate("");
    setCycle("");
  };

  const getEmptyClockEvent = (process, cycle) => {
    let clockEvent = {
      process: process,
      cycleName: cycle,
      timeIntervals: [
        {
          startTime: null,
          endTime: null,
        },
      ],
    };
    return clockEvent;
  };

  const addRemainingProcessesForClockEvents = (viewModelOpsClock) => {
    const cycleToProcessMap = new Map();
    const cycleToProcessClockMap = new Map();

    allCycles.forEach((cycle) => {
      cycleToProcessMap.set(cycle.cycleName, []);
      cycleToProcessClockMap.set(cycle.cycleName, []);
    });

    const opsClock = JSON.parse(JSON.stringify(viewModelOpsClock));

    if (opsClock.clockEvents && Array.isArray(opsClock.clockEvents)) {
      for (let i = 0; i < opsClock.clockEvents.length; i++) {
        if (
          cycleToProcessMap.get(opsClock.clockEvents[i].cycleName) !== undefined
        ) {
          cycleToProcessMap.set(opsClock.clockEvents[i].cycleName, [
            ...cycleToProcessMap.get(opsClock.clockEvents[i].cycleName),
            opsClock.clockEvents[i].process,
          ]);
          cycleToProcessClockMap.set(opsClock.clockEvents[i].cycleName, [
            ...cycleToProcessClockMap.get(opsClock.clockEvents[i].cycleName),
            opsClock.clockEvents[i],
          ]);
        }
      }
    }

    cycleToProcessMap.forEach((processes, cycle) => {
      let newClockEvents = [];
      if (!processes.includes("INDUCT")) {
        newClockEvents.push(getEmptyClockEvent("INDUCT", cycle));
      }
      if (!processes.includes("STOW")) {
        newClockEvents.push(getEmptyClockEvent("STOW", cycle));
      }
      if (!processes.includes("DISPATCH")) {
        newClockEvents.push(getEmptyClockEvent("DISPATCH", cycle));
      }
      if (!processes.includes("PICK")) {
        newClockEvents.push(getEmptyClockEvent("PICK", cycle));
      }
      cycleToProcessClockMap.set(cycle, [
        ...cycleToProcessClockMap.get(cycle),
        ...newClockEvents,
      ]);
    });

    let sortedClockEvents = [];

    cycleToProcessClockMap.forEach((processes, cycle) => {
      let clockEvents = [];
      const processMap = new Map();
      processes.forEach((clockEvent) => {
        processMap.set(clockEvent.process, clockEvent);
      });

      clockEvents.push(processMap.get("INDUCT"));
      clockEvents.push(processMap.get("STOW"));
      clockEvents.push(processMap.get("PICK"));
      clockEvents.push(processMap.get("DISPATCH"));
      if (processMap.has("TWILIGHT"))
        clockEvents.push(processMap.get("TWILIGHT"));
      sortedClockEvents.push(...clockEvents);
    });

    opsClock.clockEvents = [...sortedClockEvents];

    return opsClock;
  };

  const addRemainingProcessesForOverrides = (viewModelOpsClock) => {
    const cycleToProcessMap = new Map();
    const cycleToProcessClockMap = new Map();

    allCycles.forEach((cycle) => {
      cycleToProcessMap.set(cycle.cycleName, []);
      cycleToProcessClockMap.set(cycle.cycleName, []);
    });

    const opsClock = JSON.parse(JSON.stringify(viewModelOpsClock));

    // Initialize overrideClockEvents if it doesn't exist
    if (!opsClock.overrideClockEvents) {
      opsClock.overrideClockEvents = [];
    }

    // If no overrides exist, copy all from clockEvents
    if (
      opsClock.overrideClockEvents.length === 0 &&
      opsClock.clockEvents &&
      Array.isArray(opsClock.clockEvents)
    ) {
      opsClock.overrideClockEvents = [...opsClock.clockEvents];
    }

    // Process existing overrideClockEvents
    if (
      opsClock.overrideClockEvents &&
      Array.isArray(opsClock.overrideClockEvents)
    ) {
      for (let i = 0; i < opsClock.overrideClockEvents.length; i++) {
        if (
          cycleToProcessMap.get(opsClock.overrideClockEvents[i].cycleName) !==
          undefined
        ) {
          cycleToProcessMap.set(opsClock.overrideClockEvents[i].cycleName, [
            ...cycleToProcessMap.get(opsClock.overrideClockEvents[i].cycleName),
            opsClock.overrideClockEvents[i].process,
          ]);
          cycleToProcessClockMap.set(
            opsClock.overrideClockEvents[i].cycleName,
            [
              ...cycleToProcessClockMap.get(
                opsClock.overrideClockEvents[i].cycleName,
              ),
              opsClock.overrideClockEvents[i],
            ],
          );
        }
      }
    }

    // Add missing processes from clockEvents if they don't exist in overrides
    const overrideMap = new Map();
    opsClock.overrideClockEvents.forEach((event) => {
      overrideMap.set(`${event.process}_${event.cycleName}`, event);
    });

    if (opsClock.clockEvents && Array.isArray(opsClock.clockEvents)) {
      opsClock.clockEvents.forEach((clockEvent) => {
        const key = `${clockEvent.process}_${clockEvent.cycleName}`;
        if (!overrideMap.has(key)) {
          opsClock.overrideClockEvents.push(clockEvent);
          if (cycleToProcessMap.get(clockEvent.cycleName) !== undefined) {
            cycleToProcessMap.set(clockEvent.cycleName, [
              ...cycleToProcessMap.get(clockEvent.cycleName),
              clockEvent.process,
            ]);
            cycleToProcessClockMap.set(clockEvent.cycleName, [
              ...cycleToProcessClockMap.get(clockEvent.cycleName),
              clockEvent,
            ]);
          }
        }
      });
    }

    // Add any remaining missing processes
    cycleToProcessMap.forEach((processes, cycle) => {
      let newClockEvents = [];
      if (!processes.includes("INDUCT")) {
        newClockEvents.push(getEmptyClockEvent("INDUCT", cycle));
      }
      if (!processes.includes("STOW")) {
        newClockEvents.push(getEmptyClockEvent("STOW", cycle));
      }
      if (!processes.includes("DISPATCH")) {
        newClockEvents.push(getEmptyClockEvent("DISPATCH", cycle));
      }
      if (!processes.includes("PICK")) {
        newClockEvents.push(getEmptyClockEvent("PICK", cycle));
      }
      cycleToProcessClockMap.set(cycle, [
        ...cycleToProcessClockMap.get(cycle),
        ...newClockEvents,
      ]);
    });

    let sortedOverrideEvents = [];

    cycleToProcessClockMap.forEach((processes, cycle) => {
      let clockEvents = [];
      const processMap = new Map();
      processes.forEach((clockEvent) => {
        processMap.set(clockEvent.process, clockEvent);
      });

      clockEvents.push(processMap.get("INDUCT"));
      clockEvents.push(processMap.get("STOW"));
      clockEvents.push(processMap.get("PICK"));
      clockEvents.push(processMap.get("DISPATCH"));
      if (processMap.has("TWILIGHT"))
        clockEvents.push(processMap.get("TWILIGHT"));
      sortedOverrideEvents.push(...clockEvents);
    });

    opsClock.overrideClockEvents = [...sortedOverrideEvents];

    return opsClock;
  };

  const addProcessesForNoOpsClockData = () => {
    let clockEvents = [];
    let date = currentDate.split("T")[0];
    let opsClock = {
      nodeId: getStationCode(),
      dispatchDate: date,
    };
    allCycles.forEach((cycle) => {
      clockEvents.push(getEmptyClockEvent("INDUCT", cycle.cycleName));
      clockEvents.push(getEmptyClockEvent("STOW", cycle.cycleName));
      clockEvents.push(getEmptyClockEvent("PICK", cycle.cycleName));
      clockEvents.push(getEmptyClockEvent("DISPATCH", cycle.cycleName));
    });

    opsClock.clockEvents = clockEvents;
    return opsClock;
  };

  useEffect(() => {
    const checkCountryCode = async () => {
      const result = await getCountryCode();
      const countryCode =
        result && result.locale ? JSON.parse(result.locale) : result;
      setIsUploadEnabled(
        OPSCLOCK_UPLOAD_ENABLED_COUNTRIES.includes(countryCode),
      );
    };
    checkCountryCode();
  }, []);

  useEffect(() => {
    if (
      viewModel.opsClockData.opsClock &&
      viewModel.opsClockData.opsClock.clockEvents
    ) {
      // `allCycles` gets its data from CAS + SPCS by way of Boson.
      // This change will replace that data with SCMS data by way of LMOCS
      // since SettingsV2 is used in shift planning. We don't want to plan
      // for cycles that aren't in SCMS.
      const existingCycleSet = new Set();
      allCycles.length = 0;

      // Check if clockEvents exists before trying to iterate
      if (
        viewModel.opsClockData.opsClock.clockEvents &&
        Array.isArray(viewModel.opsClockData.opsClock.clockEvents)
      ) {
        viewModel.opsClockData.opsClock.clockEvents.forEach((clockEvent) =>
          existingCycleSet.add(clockEvent.cycleName),
        );
      }

      existingCycleSet.forEach((existingCycle) => {
        allCycles.push({
          cycleId: existingCycle,
          cycleName: existingCycle,
        });
      });

      // Determine which data source to use for display
      const hasOverrides =
        viewModel.opsClockData.opsClock.overrideClockEvents &&
        viewModel.opsClockData.opsClock.overrideClockEvents.length > 0;

      let opsClockModified;
      if (hasOverrides) {
        // For display when overrides exist
        opsClockModified = addRemainingProcessesForOverrides(
          viewModel.opsClockData.opsClock,
        );
      } else {
        // For display when no overrides (use clockEvents)
        opsClockModified = addRemainingProcessesForClockEvents(
          viewModel.opsClockData.opsClock,
        );
      }

      const oldOpsClockData = JSON.parse(JSON.stringify(opsClockModified));
      setOpsClockNewData(opsClockModified);
      setOpsClockOldData(oldOpsClockData);
      setCurrentDate(viewModel.opsClockData.opsClock.dispatchDate);
    } else {
      let opsClockData = addProcessesForNoOpsClockData();
      const newOpsClockData = JSON.parse(JSON.stringify(opsClockData));
      setOpsClockNewData(newOpsClockData);
      setOpsClockOldData(opsClockData);
    }

    const timeZone = viewModel.opsClockData.nodeTimeZone
      ? viewModel.opsClockData.nodeTimeZone
      : Intl.DateTimeFormat().resolvedOptions().timeZone;
    setTimeZone(timeZone);
  }, [viewModel.opsClockData.opsClock]);

  const getOldTimeData = (clockEvent, type) => {
    if (!opsClockOldData) {
      return null;
    }

    // For "Scheduled Time" - check overrides first, then clockEvents
    const hasOverrides =
      opsClockOldData.overrideClockEvents &&
      opsClockOldData.overrideClockEvents.length > 0;

    let sourceEvents;
    if (hasOverrides) {
      sourceEvents = opsClockOldData.overrideClockEvents;
    } else {
      sourceEvents = opsClockOldData.clockEvents;
    }

    if (!sourceEvents || !Array.isArray(sourceEvents)) {
      return null;
    }

    let opsClock = sourceEvents.filter(
      (clockE) =>
        clockE.process === clockEvent.process &&
        clockE.cycleName === clockEvent.cycleName,
    );

    // Check if we found a matching clock event
    if (
      opsClock.length === 0 ||
      !opsClock[0].timeIntervals ||
      opsClock[0].timeIntervals.length === 0
    ) {
      return null;
    }

    if (type === "startTime") {
      return opsClock[0].timeIntervals[0].startTime;
    } else {
      return opsClock[0].timeIntervals[0].endTime;
    }
  };

  let prevStartTime = null;

  return (
    <Column spacing="small" className="opsClock">
      <Row
        spacingInset="small 400"
        backgroundColor="secondary"
        width={650}
        className="opsClockFilter"
      >
        <div className="dateAndCycleFilter">
          <DatePicker
            value={currentDate}
            onChange={handleChangeDispatchDate}
            label=""
            width={170}
          />
          <div style={{ width: "10px" }}></div>
          <Select value={cycle} onChange={setCycle} width={130} size="medium">
            {allCycles.map((cycle) => {
              return (
                <SelectOption
                  value={cycle.cycleName}
                  label={cycle.cycleName}
                  key={cycle.cycleName}
                />
              );
            })}
          </Select>
        </div>
        <div className="opsClockFilterButtons">
          <Button
            type="tertiary"
            className="opsClockFilterButton"
            disabled={
              currentDate === "" ||
              cycle === "" ||
              !isUserAuthorisedForSettingsEdit
            }
            onClick={handleRemoveAllFilters}
          >
            Cancel
          </Button>
          <Button
            className="opsClockFilterButton"
            disabled={
              currentDate === "" ||
              cycle === "" ||
              opsClockNewData == null ||
              !isUserAuthorisedForSettingsEdit ||
              viewModel.opsClockErrors.length > 0
            }
            onClick={handleUpdateOpsClock}
          >
            Save
          </Button>
          {isUserAuthorisedForSettingsEdit && isUploadEnabled && (
            <OpsClockUpload
              dispatch={dispatch}
              isUserAuthorisedForSettingsEdit={isUserAuthorisedForSettingsEdit}
              handleClockEventTimeOverride={handleClockEventTimeOverride}
              addRemainingProcesses={addRemainingProcessesForClockEvents}
              handleUpdateOpsClock={handleUpdateOpsClock}
              timeZone={timeZone}
              currentDate={currentDate}
              viewModel={viewModel}
            />
          )}
        </div>
      </Row>

      <Row width={565}>
        {opsClockOldData ? (
          <Table
            headerRows={1}
            showStripes={false}
            showDividers={true}
            spacing="small"
          >
            <TableRow>
              <TableCell>Activity</TableCell>
              <TableCell>Scheduled Time</TableCell>
              <TableCell>
                <div className="overrideTime">
                  <Text className="overrideTimeText">Override Time </Text>
                  <Tooltip position="right" title={timeZone} id="overrideTime">
                    <Text>
                      <Icon tokens={infoKnockoutTokens} />
                    </Text>
                  </Tooltip>
                </div>
              </TableCell>
            </TableRow>

            {opsClockNewData
              ? (() => {
                  // Determine which data source to use for rendering
                  const hasOverrides =
                    opsClockNewData.overrideClockEvents &&
                    opsClockNewData.overrideClockEvents.length > 0;

                  const eventsToRender = hasOverrides
                    ? opsClockNewData.overrideClockEvents
                    : opsClockNewData.clockEvents;

                  return eventsToRender
                    ? eventsToRender
                        .filter((ce) => ce.cycleName === cycle)
                        .sort((a, b) => {
                          return (
                            PROCESS_ORDER[a.process] - PROCESS_ORDER[b.process]
                          );
                        })
                        .map((ce, ceIndex) => {
                          const rows = [];
                          ce.timeIntervals.forEach((ti, tiIndex) => {
                            let oldStartTime = getOldTimeData(ce, "startTime");
                            let oldEndTime = getOldTimeData(ce, "endTime");

                            rows.push(
                              <TableRow
                                key={ce.process + ce.cycleName + "startTime"}
                              >
                                <OpsClockProcessData
                                  clockEvent={ce}
                                  currentTime={ti.startTime}
                                  lowerLimit={prevStartTime}
                                  upperLimit={ti.endTime}
                                  selectedDate={currentDate}
                                  oldTime={oldStartTime}
                                  timeZone={timeZone}
                                  dispatchDate={opsClockNewData.dispatchDate}
                                  handleClockEventTimeOverride={
                                    handleClockEventTimeOverride
                                  }
                                  timeType="startTime"
                                  isUserAuthorisedForSettingsEdit={
                                    isUserAuthorisedForSettingsEdit
                                  }
                                />
                              </TableRow>,
                            );

                            rows.push(
                              <TableRow
                                key={ce.process + ce.cycleName + "endTime"}
                              >
                                <OpsClockProcessData
                                  clockEvent={ce}
                                  currentTime={ti.endTime}
                                  lowerLimit={ti.startTime}
                                  selectedDate={currentDate}
                                  oldTime={oldEndTime}
                                  timeZone={timeZone}
                                  dispatchDate={opsClockNewData.dispatchDate}
                                  handleClockEventTimeOverride={
                                    handleClockEventTimeOverride
                                  }
                                  timeType="endTime"
                                  isUserAuthorisedForSettingsEdit={
                                    isUserAuthorisedForSettingsEdit
                                  }
                                />
                              </TableRow>,
                            );

                            prevStartTime = ti.startTime;
                          });
                          return rows;
                        })
                    : null;
                })()
              : null}
          </Table>
        ) : (
          <Text color={"secondary"}>
            <FormattedMessage
              id="opsClock"
              defaultMessage={"No ops clock data found"}
            />
          </Text>
        )}
      </Row>

      <UpdatePlanResponse />
    </Column>
  );
};

export default OpsClockView;



// WEBPACK FOOTER //
// ./src/components/settings/settingsv2/OpsClockView.js